import React, { useState, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Chip from "@mui/material/Chip";
import Request from "../Utils/Request/Request";
import { showToast } from "../Utils/index";
import { AppStorage } from "../Utils/Context/AppContext";
import { useTranslation } from "react-i18next";

// ─── Shared TextField style matching the project's existing forms ─────────────
const fieldSx = {
  "& .MuiInputLabel-shrink": { color: "#41c1ba !important" },
  "& fieldset": {
    borderColor: "#41c1ba !important",
    "& legend": { "& span": { color: "#41c1ba !important" } },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#41c1ba" },
  width: "100%",
};

const BUSINESS_CATEGORIES = [
  "Commercial Services",
  "Retail",
  "Manufacturing",
  "Professional Services",
  "Healthcare",
  "Education",
  "Food & Beverage",
  "Technology",
  "Transportation",
  "Construction",
  "Other",
];

const EMPTY_FORM = {
  common_name: "",
  org_name: "",
  org_unit_name: "",
  org_identifier: "",
  invoice_type: "1100",
  address: "",
  business_category: "",
  otp: "",
};

const BUSINESS_DETAIL_FIELDS = [
  "common_name",
  "org_name",
  "org_unit_name",
  "org_identifier",
  "address",
  "business_category",
];

function getStatusDetails(response) {
  const payload = response?.data?.data || response?.data || {};
  const hasConfigEnvelope = Object.prototype.hasOwnProperty.call(payload, "config");
  return {
    environment: payload.zatca_environment || payload.config?.zatca_environment || "Simulation",
    config: hasConfigEnvelope ? payload.config : payload,
  };
}

// ─── Status badge helper ───────────────────────────────────────────────────────
function StatusBadge({ status, t }) {
  const map = {
    COMPLETED:   { label: t("Integrated ✓"),    color: "success" },
    IN_PROGRESS: { label: t("In Progress…"),    color: "warning" },
    FAILED:      { label: t("Failed"),          color: "error"   },
    PENDING:     { label: t("Not Integrated"),  color: "default" },
    null:        { label: t("Not Integrated"),  color: "default" },
  };
  const cfg = map[status] || map["null"];
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      sx={{ fontWeight: 600, fontSize: "13px", px: 1 }}
    />
  );
}

function CertificateBadge({ status, t }) {
  const map = {
    VALID: { label: t("Valid"), color: "success" },
    EXPIRING_SOON: { label: t("Expiring Soon"), color: "warning" },
    EXPIRED: { label: t("Expired"), color: "error" },
    NOT_ONBOARDED: { label: t("Not Onboarded"), color: "default" },
  };
  const cfg = map[status] || map.NOT_ONBOARDED;
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      sx={{ fontWeight: 600, fontSize: "13px", px: 1 }}
    />
  );
}

function ZatcaIntegration() {
  const AppContext = useContext(AppStorage);
  const { t } = useTranslation();

  const [loading,    setLoading]    = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsSaved, setDetailsSaved] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [environment, setEnvironment] = useState("");
  const [environmentSwitching, setEnvironmentSwitching] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [config,     setConfig]     = useState(null);   // existing DB record
  const [success,    setSuccess]    = useState(false);
  const [errorMsg,   setErrorMsg]   = useState("");
  const [form, setForm] = useState(EMPTY_FORM);

  // ── Load current status on mount ─────────────────────────────────────────────
  useEffect(() => {
    fetchStatus().catch(() => {
      showToast("error", t("Could not load the ZATCA status."));
    });
  }, []);

  async function fetchStatus() {
    setStatusLoading(true);
    try {
      const res = await Request.get("/zatca/status");
      const { environment: activeEnvironment, config: statusData } = getStatusDetails(res);
      setEnvironment(activeEnvironment);
      setConfig(statusData || null);
      const c = statusData || {};
      setDetailsSaved(BUSINESS_DETAIL_FIELDS.every((field) => String(c[field] || "").trim()));
      setForm({
        ...EMPTY_FORM,
        common_name: c.common_name || "",
        org_name: c.org_name || "",
        org_unit_name: c.org_unit_name || "",
        org_identifier: c.org_identifier || "",
        invoice_type: c.invoice_type || "1100",
        address: c.address || "",
        business_category: c.business_category || "",
      });
    } finally {
      setStatusLoading(false);
    }
  }

  async function handleEnvironmentChange(event) {
    const nextEnvironment = event.target.value;
    if (nextEnvironment === environment) return;
    if (nextEnvironment === "Production" && !window.confirm(
      t("Production invoices are submitted to the live ZATCA environment. Continue?")
    )) return;

    setEnvironmentSwitching(true);
    setErrorMsg("");
    setSuccess(false);
    try {
      await Request.put("/zatca/environment", { zatca_environment: nextEnvironment });
      setEnvironment(nextEnvironment);
      try {
        await fetchStatus();
      } catch (err) {
        showToast("error", t("The environment changed, but its data could not be reloaded."));
      }
      AppContext?.refreshZatcaEnvironmentData?.();
      showToast("success", t("ZATCA environment switched successfully."));
    } catch (err) {
      showToast("error", err?.response?.data?.message || t("Could not switch the ZATCA environment."));
    } finally {
      setEnvironmentSwitching(false);
    }
  }

  async function handleResetIntegration() {
    if (!window.confirm(t("Resetting this integration permanently removes its saved ZATCA credentials and CSR details for the selected environment. Invoice history will not be changed. Continue?"))) return;

    setResetting(true);
    setErrorMsg("");
    setSuccess(false);
    try {
      const response = await Request.delete("/zatca/integration");
      setConfig(null);
      setForm(EMPTY_FORM);
      setDetailsSaved(false);
      await fetchStatus();
      showToast("success", response.data?.message || t("ZATCA integration has been reset."));
    } catch (error) {
      showToast("error", error?.response?.data?.message || t("Could not reset ZATCA integration."));
    } finally {
      setResetting(false);
    }
  }

  function handleChange(field, value) {
    if (BUSINESS_DETAIL_FIELDS.includes(field)) setDetailsSaved(false);
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function formatCertificateDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  function openRenewalForm() {
    setSuccess(false);
    setErrorMsg("");
    setForm((prev) => ({ ...prev, otp: "" }));
  }

  function validateBusinessDetails() {
    const required = {
      common_name:       "Business Name (Common Name)",
      org_name:          "Business Legal Name",
      org_unit_name:     "Organization Unit / Branch ID",
      org_identifier:    "Registered VAT Number",
      address:           "Registered City / Address",
      business_category: "Business Category",
    };
    for (const [key, label] of Object.entries(required)) {
      if (!form[key]?.trim()) {
        showToast("error", `${t("Please fill in:")} ${t(label)}`);
        return false;
      }
    }
    if (form.org_identifier.trim().length !== 15) {
      showToast("error", t("VAT Number must be exactly 15 digits."));
      return false;
    }
    return true;
  }

  function validate() {
    if (!validateBusinessDetails()) return false;
    if (!detailsSaved) {
      showToast("error", t("Save Business & CSR Details before entering the OTP."));
      return false;
    }
    if (!form.otp?.trim()) {
      showToast("error", `${t("Please fill in:")} ${t("OTP")}`);
      return false;
    }
    if (!/^\d+$/.test(form.otp.trim())) {
      showToast("error", t("OTP must be numeric."));
      return false;
    }
    return true;
  }

  async function handleSaveBusinessDetails() {
    setErrorMsg("");
    setSuccess(false);
    if (!validateBusinessDetails()) return;

    setDetailsSaving(true);
    try {
      const payload = Object.fromEntries(
        [...BUSINESS_DETAIL_FIELDS, "invoice_type"].map((field) => [field, form[field]])
      );
      const res = await Request.put("/zatca/details", payload);
      setDetailsSaved(true);
      setConfig(res.data?.config || config);
      showToast("success", res.data?.message || t("Business & CSR Details saved."));
    } catch (err) {
      const msg = err?.response?.data?.message || t("Could not save Business & CSR Details.");
      setErrorMsg(msg);
      showToast("error", msg);
    } finally {
      setDetailsSaving(false);
    }
  }

  async function handleSubmit() {
    setErrorMsg("");
    setSuccess(false);
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await Request.post("/zatca/onboard", form);
      setSuccess(true);
      showToast("success", res.data.message || t("ZATCA Integration Successful!"));
      fetchStatus(); // refresh status badge
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        t("An error occurred during ZATCA onboarding. Please try again.");
      setErrorMsg(msg);
      showToast("error", msg);
      fetchStatus();
    } finally {
      setForm((prev) => ({ ...prev, otp: "" }));
      setLoading(false);
    }
  }

  const isPhase2 = Number(AppContext?.userInfo?.zatca_phase || 1) === 2;
  const isCompleted   = config?.onboarding_status === "COMPLETED";
  const isInProgress  = config?.onboarding_status === "IN_PROGRESS" || loading;
  const certificateStatus = config?.certificate_status || (isCompleted ? "VALID" : "NOT_ONBOARDED");
  const certificateExpiryDate = formatCertificateDate(config?.certificate_expires_at);
  const certificateIssuedDate = formatCertificateDate(config?.certificate_issued_at);
  const requiresRenewal = ["EXPIRING_SOON", "EXPIRED"].includes(certificateStatus);
  const canEditOnboarding = !isInProgress && (!isCompleted || requiresRenewal);
  const canResetIntegration = Boolean(config && !isInProgress && (isCompleted || config?.onboarding_status === "FAILED" || detailsSaved));

  if (!isPhase2) {
    return (
      <Box>
        <Typography variant="h4" sx={{ paddingBottom: "5px", color: "#365b6d" }}>
          {t("ZATCA Integration")}
        </Typography>
        <Divider variant="middle" sx={{ mb: 2 }} />
        <Alert severity="info" sx={{ maxWidth: 800 }}>
          {t("ZATCA onboarding is only available for Phase 2 users.")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Page Header ────────────────────────────────────────────────────── */}
      <Typography variant="h4" sx={{ paddingBottom: "5px", color: "#365b6d" }}>
        {t("ZATCA Integration")}
      </Typography>
      <Divider variant="middle" sx={{ mb: 2 }} />

      <Box sx={{ maxWidth: 320, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ color: "#365b6d", mb: 0.5 }}>
          {t("ZATCA Environment")}
        </Typography>
        <FormControl size="small" fullWidth sx={fieldSx}>
          <Select
            value={environment}
            onChange={handleEnvironmentChange}
            disabled={statusLoading || environmentSwitching || !environment}
            displayEmpty
          >
            {!environment && <MenuItem value="">{t("Loading...")}</MenuItem>}
            <MenuItem value="Simulation">{t("Simulation")}</MenuItem>
            <MenuItem value="Production">{t("Production")}</MenuItem>
          </Select>
        </FormControl>
        {environmentSwitching && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption">{t("Switching environment...")}</Typography>
          </Box>
        )}
      </Box>

      {/* ── Status Card ────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          background: "#fff",
          maxWidth: 800,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: isCompleted ? "#4caf5022" : "#365b6d22",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "22px",
          }}
        >
          {isCompleted ? "✅" : "🛡️"}
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#365b6d" }}>
            {t("Onboarding Status")}
          </Typography>
          {statusLoading ? (
            <CircularProgress size={16} sx={{ color: "#365b6d" }} />
          ) : (
            <StatusBadge status={config?.onboarding_status || null} t={t} />
          )}
        </Box>
      </Box>

      {config?.error_message && (
        <Alert severity="error" sx={{ mb: 3, maxWidth: 800, whiteSpace: "pre-wrap" }}>
          {config.error_message}
        </Alert>
      )}

      {/* ── Certificate Status ─────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          background: "#fff",
          maxWidth: 800,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#365b6d", mb: 1 }}>
          {t("Certificate Status")}
        </Typography>
        {statusLoading ? (
          <CircularProgress size={16} sx={{ color: "#365b6d" }} />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <CertificateBadge status={certificateStatus} t={t} />
              {certificateStatus === "VALID" && certificateExpiryDate && (
                <Typography variant="body2" sx={{ color: "#555" }}>
                  {t("Expires on")} {certificateExpiryDate}
                </Typography>
              )}
              {certificateIssuedDate && (
                <Typography variant="caption" sx={{ color: "#777" }}>
                  {t("Issued on")} {certificateIssuedDate}
                </Typography>
              )}
            </Box>
            {certificateStatus === "EXPIRING_SOON" && (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={openRenewalForm}>
                    {t("Renew")}
                  </Button>
                }
              >
                {t("ZATCA certificate renewal is needed soon. Please renew onboarding credentials before the certificate expires.")}
                {certificateExpiryDate ? ` ${t("Expires on")} ${certificateExpiryDate}.` : ""}
              </Alert>
            )}
            {certificateStatus === "EXPIRED" && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={openRenewalForm}>
                    {t("Renew")}
                  </Button>
                }
              >
                <AlertTitle>{t("ZATCA Certificate Expired")}</AlertTitle>
                {t("ZATCA certificate expired. Renew onboarding credentials before creating or reporting Phase 2 invoices.")}
              </Alert>
            )}
          </Box>
        )}
      </Box>

      {/* ── Success Banner ──────────────────────────────────────────────────── */}
      {success && (
        <Alert severity="success" sx={{ mb: 3, maxWidth: 800 }}>
          {t("ZATCA Integration Complete! Your account is now fully integrated with ZATCA. All credentials have been saved securely.")}
        </Alert>
      )}

      {/* ── Error Banner ────────────────────────────────────────────────────── */}
      {errorMsg && (
        <Alert severity="error" sx={{ mb: 3, maxWidth: 800, whiteSpace: "pre-wrap" }}>
          {errorMsg}
        </Alert>
      )}

      {/* ── Loading Banner (during onboarding) ─────────────────────────────── */}
      {loading && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 3, maxWidth: 800 }}>
          {t("Onboarding with ZATCA… please wait. This process runs 6 compliance checks and may take up to 2 minutes. Do not close this page.")}
        </Alert>
      )}

      {/* ── Form ────────────────────────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 800 }}>
        <Typography variant="h6" sx={{ color: "#365b6d", mb: 1 }}>
          {requiresRenewal ? t("Renew ZATCA Onboarding") : t("Business & CSR Details")}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          {/* Business Name (Common Name) */}
          <Grid item xs={12} sm={6} sx={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: 4, fontSize: 13, color: "#555" }}>
              {t("Business Name (Common Name)")} <span style={{ color: "red" }}>*</span>
            </label>
            <TextField
              size="small"
              placeholder={t("e.g. Acme Trading - Riyadh")}
              value={form.common_name}
              onChange={(e) => handleChange("common_name", e.target.value)}
              disabled={!canEditOnboarding}
              sx={fieldSx}
            />
          </Grid>

          {/* Business Legal Name */}
          <Grid item xs={12} sm={6} sx={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: 4, fontSize: 13, color: "#555" }}>
              {t("Business Legal Name (Arabic / English)")} <span style={{ color: "red" }}>*</span>
            </label>
            <TextField
              size="small"
              placeholder={t("e.g. Acme Trading Establishment")}
              value={form.org_name}
              onChange={(e) => handleChange("org_name", e.target.value)}
              disabled={!canEditOnboarding}
              sx={fieldSx}
              inputProps={{ dir: "auto" }}
            />
          </Grid>

          {/* Organization Unit (Branch ID / CR) */}
          <Grid item xs={12} sm={6} sx={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: 4, fontSize: 13, color: "#555" }}>
              {t("Organization Unit / Branch ID")} <span style={{ color: "red" }}>*</span>
            </label>
            <TextField
              size="small"
              placeholder={t("e.g. 3000000000")}
              value={form.org_unit_name}
              onChange={(e) => handleChange("org_unit_name", e.target.value)}
              disabled={!canEditOnboarding}
              sx={fieldSx}
            />
          </Grid>

          {/* Registered VAT Number (15 digits) */}
          <Grid item xs={12} sm={6} sx={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: 4, fontSize: 13, color: "#555" }}>
              {t("Registered VAT Number (15 digits)")} <span style={{ color: "red" }}>*</span>
            </label>
            <TextField
              size="small"
              placeholder={t("e.g. 300000000000000")}
              value={form.org_identifier}
              onChange={(e) => handleChange("org_identifier", e.target.value)}
              disabled={!canEditOnboarding}
              inputProps={{ maxLength: 15 }}
              sx={fieldSx}
            />
          </Grid>

          {/* Business Category */}
          <Grid item xs={12} sm={6} sx={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: 4, fontSize: 13, color: "#555" }}>
              {t("Business Category")} <span style={{ color: "red" }}>*</span>
            </label>
            <FormControl size="small" sx={fieldSx}>
              <Select
                value={form.business_category}
                onChange={(e) => handleChange("business_category", e.target.value)}
                disabled={!canEditOnboarding}
                displayEmpty
                renderValue={(v) => v ? t(v) : <span style={{ color: "#aaa" }}>{t("Select category…")}</span>}
              >
                {BUSINESS_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {t(cat)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Registered City / Address */}
          <Grid item xs={12} sm={6} sx={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: 4, fontSize: 13, color: "#555" }}>
              {t("Registered City / Address")} <span style={{ color: "red" }}>*</span>
            </label>
            <TextField
              size="small"
              placeholder={t("e.g. Riyadh")}
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
              disabled={!canEditOnboarding}
              sx={fieldSx}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, mb: 3 }}>
          <Button
            onClick={handleSaveBusinessDetails}
            variant="outlined"
            size="large"
            disabled={!canEditOnboarding || detailsSaving}
            sx={{ borderColor: "#365b6d", color: "#365b6d", minWidth: 260 }}
          >
            {detailsSaving ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                {t("Saving Business & CSR Details...")}
              </Box>
            ) : (
              t("Save Business & CSR Details")
            )}
          </Button>
        </Box>

        {/* ── OTP Section ─────────────────────────────────────────────────────── */}
        <Typography variant="h6" sx={{ color: "#365b6d", mt: 3, mb: 1 }}>
          {t("ZATCA OTP")}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} sx={{ display: "flex", flexDirection: "column" }}>
            <label style={{ marginBottom: 4, fontSize: 13, color: "#555" }}>
              {t("OTP (from Fatoora Portal)")} <span style={{ color: "red" }}>*</span>
            </label>
            <TextField
              size="small"
              placeholder={t("6-digit OTP")}
              value={form.otp}
              onChange={(e) => handleChange("otp", e.target.value)}
              disabled={!canEditOnboarding || !detailsSaved}
              inputProps={{ maxLength: 10 }}
              sx={fieldSx}
            />
            <Typography variant="caption" sx={{ color: "#888", mt: 0.5 }}>
              {t("Generate this code from the Fatoora portal (portal.zatca.gov.sa) under CSID Onboarding > New CSID.")}
            </Typography>
            {!detailsSaved && (
              <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5 }}>
                {t("Save Business & CSR Details before entering the OTP.")}
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* ── Submit Button ────────────────────────────────────────────────────── */}
        <Box sx={{ mt: 3, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          {(!isCompleted || requiresRenewal) && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              size="large"
              disabled={loading || isInProgress || !detailsSaved}
              sx={{
                backgroundColor: "#365b6d",
                "&:hover": { backgroundColor: "#2a4a59" },
                minWidth: 260,
                position: "relative",
              }}
            >
              {loading || isInProgress ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                  {t("Onboarding with ZATCA… please wait")}
                </Box>
              ) : (
                requiresRenewal ? t("Renew ZATCA Onboarding") : t("Submit & Integrate with ZATCA")
              )}
            </Button>
          )}

          {isCompleted && !requiresRenewal && (
            <Button
              variant="contained"
              size="large"
              disabled
              sx={{
                backgroundColor: "#4caf50",
                "&:hover": { backgroundColor: "#4caf50" },
                "&.Mui-disabled": { backgroundColor: "#4caf5099", color: "#fff" },
                minWidth: 260,
              }}
            >
              {t("Already Integrated with ZATCA")}
            </Button>
          )}
          {canResetIntegration && (
            <Button
              onClick={handleResetIntegration}
              variant="outlined"
              color="error"
              size="large"
              disabled={resetting}
            >
              {resetting ? t("Resetting...") : t("Reset ZATCA Integration")}
            </Button>
          )}
        </Box>

        {/* ── Info note ────────────────────────────────────────────────────────── */}
        <Box sx={{ mt: 2, maxWidth: 700 }}>
          <Typography variant="caption" sx={{ color: "#888" }}>
            <strong>{t("Note:")}</strong> {t("The onboarding process involves 6 compliance checks and typically takes 60-120 seconds. All credentials (private key, CSID tokens) are stored securely in the database and are never displayed here. Keep this page open until the process completes.")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default ZatcaIntegration;
